if (!customElements.get('dole-inquiry')) {
  class DoleInquiry extends HTMLElement {
    static continuousPath(width, height, radius) {
      const w = Math.max(0, width), h = Math.max(0, height);
      const r = Math.min(Number.isFinite(radius) ? Math.max(0, radius) : 0, w / 2, h / 2);
      const A = 0.43416567446713084, B = 0.681792830507429, M = 0.8408964152537145, Q = 1 - M;
      const commands = [];
      const point = p => p.map(n => Number(n.toFixed(8))).join(' ');
      const corner = map => {
        if (!r) { commands.push('L ' + point(map(1, 1))); return; }
        commands.push('C ' + [map(A, 0), map(B, 0), map(M, Q)].map(point).join(' '));
        commands.push('C ' + [map(1, 2 * Q), map(1, 1 - A), map(1, 1)].map(point).join(' '));
      };
      commands.push(`M ${r} 0`, `L ${w - r} 0`);
      corner((u, v) => [w - r + u * r, v * r]);
      commands.push(`L ${w} ${h - r}`);
      corner((u, v) => [w - v * r, h - r + u * r]);
      commands.push(`L ${r} ${h}`);
      corner((u, v) => [r - u * r, h - v * r]);
      commands.push(`L 0 ${r}`);
      corner((u, v) => [v * r, r - u * r]);
      return commands.join(' ') + ' Z';
    }

    static scrollToElement(el) {
      if (!el || typeof window === 'undefined') return;
      const navHeight = (typeof getComputedStyle !== 'undefined' && typeof document !== 'undefined' && document.documentElement)
        ? (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '72', 10) || 72)
        : 72;
      const rect = (typeof el.getBoundingClientRect === 'function') ? el.getBoundingClientRect() : { top: 0 };
      const currentScrollY = Number.isFinite(window.scrollY) ? window.scrollY : 0;
      const targetY = Math.max(0, currentScrollY + rect.top - navHeight - 20);
      if (typeof window.scrollTo === 'function') {
        window.scrollTo({
          top: targetY,
          behavior: (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) ? 'auto' : 'smooth'
        });
      }
    }

    connectedCallback() {
      if (this.controller) return;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.messages = JSON.parse(this.querySelector('[data-messages]').textContent);
      this.feedback = this.querySelector('[data-feedback]');
      this.nextSteps = this.querySelector('[data-next-steps]');
      this.loadingEl = this.querySelector('[data-loading]');
      this.loadingTitle = this.querySelector('[data-loading-title]');
      this.loadingStatus = this.querySelector('[data-loading-status]');
      this.loadingBar = this.querySelector('[data-loading-bar]');
      this.loadingTimers = [];
      this.summary = this.querySelector('[data-summary]');
      this.preview = this.querySelector('[data-message]');
      this.copyStatus = this.querySelector('[data-copy-status]');
      this.updateRequiredNames = () => {
        this.names = [...new Set([...this.form.querySelectorAll('[required]')].map(input => input.dataset.field || input.name))];
      };
      this.updateRequiredNames();

      const venueRadios = this.querySelectorAll('[name="training_venue"], [name="contact[training_venue]"]');
      if (venueRadios.length) {
        const onsiteWrapper = this.querySelector('[data-onsite-wrapper]');
        const onsiteInput = this.querySelector('[data-field="training_location"], [name="training_location"], [name="contact[training_location]"]');
        const syncVenue = () => {
          const isVenueOnsite = this.querySelector('[name="training_venue"]:checked, [name="contact[training_venue]"]:checked')?.value === 'onsite';
          if (onsiteWrapper && onsiteInput) {
            onsiteWrapper.hidden = !isVenueOnsite;
            onsiteInput.required = isVenueOnsite;
            if (!isVenueOnsite) {
              onsiteInput.removeAttribute('aria-invalid');
              const err = this.querySelector('[data-error="training_location"]');
              if (err) { err.textContent = ''; err.hidden = true; }
            } else {
              onsiteInput.focus();
            }
            this.updateRequiredNames();
            this.setupShapes();
          }
        };
        venueRadios.forEach(radio => radio.addEventListener('change', syncVenue, options));
      }

      const quoteCard = this.querySelector('[data-quote-card]');
      if (quoteCard) {
        const quoteAmount = this.querySelector('[data-quote-amount]');
        const quoteDetails = this.querySelector('[data-quote-details]');
        const participantsInput = this.querySelector('[data-field="participants"], [name="participants"], [name="contact[participants]"]');
        const courseRadios = this.querySelectorAll('[data-field="training_type"], [name="training_type"], [name="contact[training_type]"]');

        const updateQuote = () => {
          const checkedCourse = this.querySelector('[data-field="training_type"]:checked, [name="training_type"]:checked, [name="contact[training_type]"]:checked');
          const count = parseInt(participantsInput?.value, 10);
          const rate = checkedCourse ? parseInt(checkedCourse.dataset.rate, 10) : 0;
          const courseCode = checkedCourse ? checkedCourse.dataset.course : '';

          if (checkedCourse && !isNaN(count) && count > 0) {
            const total = count * rate;
            quoteAmount.textContent = '₱' + total.toLocaleString('en-PH');
            quoteDetails.textContent = `${count} participant${count === 1 ? '' : 's'} × ₱${rate.toLocaleString('en-PH')} / head (${courseCode})`;
            quoteCard.classList.add('dole-inquiry__quote--active');
          } else if (checkedCourse) {
            quoteAmount.textContent = '₱' + rate.toLocaleString('en-PH');
            quoteDetails.textContent = `₱${rate.toLocaleString('en-PH')} per head · Enter number of participants for ${courseCode}`;
            quoteCard.classList.remove('dole-inquiry__quote--active');
          } else {
            quoteAmount.textContent = '₱0';
            quoteDetails.textContent = this.messages.quote_prompt || 'Select a course and enter participants';
            quoteCard.classList.remove('dole-inquiry__quote--active');
          }
          this.setupShapes();
        };

        courseRadios.forEach(radio => radio.addEventListener('change', updateQuote, options));
        participantsInput?.addEventListener('input', updateQuote, options);
        participantsInput?.addEventListener('change', updateQuote, options);
        updateQuote();
      }

      this.form.addEventListener('submit', event => this.submit(event), options);
      this.form.addEventListener('focusout', event => {
        if (event.target.matches('input[required], textarea[required]')) this.validate(event.target.dataset.field || event.target.name);
      }, options);
      const change = event => {
        if (!event.target.matches('input[required], textarea[required]')) return;
        const fieldName = event.target.dataset.field || event.target.name;
        if (event.target.getAttribute('aria-invalid') === 'true' || event.target.type === 'radio') this.validate(fieldName);
        if (!this.feedback.hidden && this.dataset.submissionMode === 'email_draft') {
          this.feedback.hidden = true;
          this.querySelector('[data-open-email]')?.removeAttribute('href');
          this.preview.value = '';
          this.copyStatus.textContent = '';
          this.copyVersion = (this.copyVersion || 0) + 1;
        }
        if (!this.form.querySelector('[aria-invalid="true"]')) this.summary.textContent = '';
      };
      this.form.addEventListener('input', change, options);
      this.form.addEventListener('change', change, options);
      const submitFrame = this.querySelector('iframe[data-submit-frame]');
      if (submitFrame && this.form) {
        this.form.target = submitFrame.name;
      }

      this.querySelector('[data-copy]')?.addEventListener('click', () => this.copy(), options);
      this.querySelector('[data-reset]')?.addEventListener('click', () => this.resetForm(), options);
      this.querySelector('[data-retry]')?.addEventListener('click', () => this.retrySubmit(), options);
      this.querySelector('[data-edit]')?.addEventListener('click', () => this.editDetails(), options);
      this.setupShapes();

      if (typeof window !== 'undefined' && window.location && window.location.search && window.location.search.includes('contact_posted=true')) {
        const scrollToFeedback = () => {
          const feedbackEl = this.querySelector('.dole-inquiry__feedback') || this.querySelector('.dole-inquiry__panel') || this;
          DoleInquiry.scrollToElement(feedbackEl);
          feedbackEl?.focus?.({ preventScroll: true });
        };
        requestAnimationFrame(() => {
          scrollToFeedback();
          setTimeout(scrollToFeedback, 250);
        });
        if (window.history && window.history.replaceState) {
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        }
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.clearLoadingTimers?.();
      this.copyVersion = (this.copyVersion || 0) + 1;
    }

    setupShapes() {
      if (!('ResizeObserver' in window)) return;
      const render = element => {
        const w = element.clientWidth, h = element.clientHeight;
        if (w < 2 || h < 2) return;
        let svg = element.querySelector(':scope > .dole-shape');
        if (!svg) {
          svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          svg.setAttribute('class', 'dole-shape');
          svg.setAttribute('aria-hidden', 'true');
          svg.setAttribute('focusable', 'false');
          const halo = document.createElementNS(svg.namespaceURI, 'path');
          halo.setAttribute('class', 'dole-shape__halo');
          halo.setAttribute('transform', 'translate(0.5 0.5)');
          const surface = document.createElementNS(svg.namespaceURI, 'path');
          surface.setAttribute('class', 'dole-shape__surface');
          surface.setAttribute('transform', 'translate(0.5 0.5)');
          svg.append(halo, surface);
          element.prepend(svg);
        } else if (!svg.querySelector('.dole-shape__halo')) {
          svg.innerHTML = '';
          const halo = document.createElementNS(svg.namespaceURI, 'path');
          halo.setAttribute('class', 'dole-shape__halo');
          halo.setAttribute('transform', 'translate(0.5 0.5)');
          const surface = document.createElementNS(svg.namespaceURI, 'path');
          surface.setAttribute('class', 'dole-shape__surface');
          surface.setAttribute('transform', 'translate(0.5 0.5)');
          svg.append(halo, surface);
        }
        svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
        const d = DoleInquiry.continuousPath(w - 1, h - 1, Number(element.dataset.radius) - .5);
        svg.querySelectorAll('path').forEach(path => path.setAttribute('d', d));
        element.setAttribute('data-shaped', 'continuous-g2-v1');
      };
      this.resizeObserver = new ResizeObserver(entries => entries.forEach(entry => render(entry.target)));
      this.querySelectorAll('.dole-surface').forEach(element => { render(element); this.resizeObserver.observe(element); });
    }

    validate(name) {
      const cleanName = (name || '').replace(/^contact\[(.*?)\]$/, '$1');
      const controls = [...this.form.querySelectorAll(`[data-field="${cleanName}"], [name="${name}"], [name="contact[${cleanName}]"]`)];
      if (!controls.length) return true;
      const input = controls[0];
      let error = '';
      if (input.type === 'radio') {
        if (!controls.some(control => control.checked)) error = this.messages[cleanName] || this.messages.course || this.messages.required;
      } else if (!input.value.trim()) {
        error = this.messages.required;
      } else if (!input.validity.valid) {
        error = this.messages[cleanName] || this.messages.required;
      }
      controls.forEach(control => error ? control.setAttribute('aria-invalid', 'true') : control.removeAttribute('aria-invalid'));
      const message = this.querySelector(`[data-error="${cleanName}"]`);
      if (message) {
        message.textContent = error;
        message.hidden = !error;
      }
      return !error;
    }

    static extractData(formOrData) {
      let entries;
      if (typeof FormData !== 'undefined' && formOrData instanceof FormData) {
        entries = Object.fromEntries(formOrData);
      } else if (typeof HTMLFormElement !== 'undefined' && formOrData instanceof HTMLFormElement) {
        entries = Object.fromEntries(new FormData(formOrData));
      } else if (formOrData && typeof formOrData === 'object') {
        entries = formOrData;
      } else {
        entries = {};
      }
      const data = {};
      for (const [rawKey, val] of Object.entries(entries)) {
        const cleanKey = rawKey.replace(/^contact\[(.*?)\]$/, '$1');
        data[cleanKey] = typeof val === 'string' ? val.trim() : val;
      }
      return data;
    }

    async submit(event) {
      event.preventDefault();
      this.updateRequiredNames?.();
      const valid = this.names.map(name => this.validate(name)).every(Boolean);
      if (!valid) {
        if (this.feedback) this.feedback.hidden = true;
        this.summary.textContent = this.messages.summary;
        const invalid = this.form.querySelector('[aria-invalid="true"]');
        if (invalid) invalid.focus();
        return;
      }
      this.summary.textContent = '';
      const data = DoleInquiry.extractData(this.form);
      const { subject, body } = DoleInquiry.buildEmail(data, this.dataset.inquiryType, this.dataset.membership, this.dataset.source);
      const recipient = this.dataset.recipient;

      const fullName = (data.first_name && data.last_name)
        ? `${data.first_name} ${data.last_name}`.trim()
        : (data.name || `${data.first_name || ''} ${data.last_name || ''}`.trim());
      const mobileNumber = data.mobile || data.phone || '';

      const nameInput = this.form.querySelector('[data-contact-name]');
      if (nameInput) nameInput.value = fullName;
      const phoneInput = this.form.querySelector('[data-contact-phone]');
      if (phoneInput) phoneInput.value = mobileNumber;
      const subjectInput = this.form.querySelector('[data-contact-subject]');
      if (subjectInput) subjectInput.value = subject;
      const bodyInput = this.form.querySelector('[data-contact-body]');
      if (bodyInput) bodyInput.value = body;

      this.lastRecipient = recipient;
      this.lastSubject = subject;
      this.lastBody = body;
      this.lastData = data;

      const mode = this.dataset.submissionMode || 'auto_send';
      if (mode === 'email_draft') {
        this.handleEmailDraft(recipient, subject, body, data);
        return;
      }

      await this.handleAutoSend(recipient, subject, body, data);
    }

    retrySubmit() {
      if (!this.lastRecipient) return;
      this.handleAutoSend(this.lastRecipient, this.lastSubject, this.lastBody, this.lastData);
    }

    editDetails() {
      this.editForm();
    }

    static escapeHTML(str) {
      return String(str || '').replace(/[&<>"']/g, match => {
        switch (match) {
          case '&': return '&amp;';
          case '<': return '&lt;';
          case '>': return '&gt;';
          case '"': return '&quot;';
          case "'": return '&#39;';
          default: return match;
        }
      });
    }

    escapeHTML(str) {
      return DoleInquiry.escapeHTML(str);
    }

    static buildSummaryHTML(data, inquiryType, recipient, fallbackMembership) {
      if (!data) return '';
      const isBusiness = inquiryType === 'business';
      const fullName = (data.first_name && data.last_name)
        ? `${data.first_name} ${data.last_name}`.trim()
        : (data.name || `${data.first_name || ''} ${data.last_name || ''}`.trim());
      const contactDirect = [data.email, data.mobile || data.phone].filter(Boolean).join(' · ');

      let detailsHTML = `
        <div class="dole-inquiry__summary-row">
          <span class="dole-inquiry__summary-label">Contact Person</span>
          <span class="dole-inquiry__summary-val">${DoleInquiry.escapeHTML(fullName)}${data.position ? ` (${DoleInquiry.escapeHTML(data.position)})` : ''}</span>
        </div>
        <div class="dole-inquiry__summary-row">
          <span class="dole-inquiry__summary-label">Direct Contact</span>
          <span class="dole-inquiry__summary-val">${DoleInquiry.escapeHTML(contactDirect)}</span>
        </div>
        <div class="dole-inquiry__summary-row">
          <span class="dole-inquiry__summary-label">Company / Organization</span>
          <span class="dole-inquiry__summary-val">${DoleInquiry.escapeHTML(data.company || 'Not specified')}</span>
        </div>
      `;

      if (isBusiness) {
        detailsHTML += `
          <div class="dole-inquiry__summary-row">
            <span class="dole-inquiry__summary-label">Membership Option</span>
            <span class="dole-inquiry__summary-val">${DoleInquiry.escapeHTML(data.membership_type || fallbackMembership || 'Corporate Membership')}</span>
          </div>
          <div class="dole-inquiry__summary-row">
            <span class="dole-inquiry__summary-label">Employees & Industry</span>
            <span class="dole-inquiry__summary-val">${DoleInquiry.escapeHTML(data.employees ? `${data.employees} employees` : '')}${data.industry ? ` · ${DoleInquiry.escapeHTML(data.industry)}` : ''}</span>
          </div>
          <div class="dole-inquiry__summary-row">
            <span class="dole-inquiry__summary-label">Company Address</span>
            <span class="dole-inquiry__summary-val">${DoleInquiry.escapeHTML(data.address || 'Address provided')}</span>
          </div>
        `;
      } else {
        const venueText = (data.training_venue === 'onsite')
          ? `On-site: ${data.training_location || 'Address specified'}`
          : 'Lifeline EMS Academy (Valgosons Building)';

        let rate = 0;
        if (/EFAT/i.test(data.training_type)) rate = 1500;
        else if (/OFAT/i.test(data.training_type)) rate = 2500;
        else if (/SFAT/i.test(data.training_type)) rate = 3500;
        const count = parseInt(data.participants, 10);
        const total = (!isNaN(count) && count > 0 && rate > 0) ? rate * count : 0;

        detailsHTML += `
          <div class="dole-inquiry__summary-row">
            <span class="dole-inquiry__summary-label">Training Course</span>
            <span class="dole-inquiry__summary-val">${DoleInquiry.escapeHTML(data.training_type || 'DOLE Certified First Aid')}</span>
          </div>
          <div class="dole-inquiry__summary-row">
            <span class="dole-inquiry__summary-label">Venue</span>
            <span class="dole-inquiry__summary-val">${DoleInquiry.escapeHTML(venueText)}</span>
          </div>
          <div class="dole-inquiry__summary-row">
            <span class="dole-inquiry__summary-label">Participants</span>
            <span class="dole-inquiry__summary-val">${count || data.participants || 0} participants</span>
          </div>
        `;

        if (total > 0) {
          detailsHTML += `
            <div class="dole-inquiry__summary-row dole-inquiry__summary-quote">
              <span class="dole-inquiry__summary-label">Estimate Total Quotation</span>
              <span class="dole-inquiry__summary-val">₱${total.toLocaleString('en-PH')} (${count} participants × ₱${rate.toLocaleString('en-PH')} / head)</span>
            </div>
          `;
        }
      }

      return `
        <div class="dole-inquiry__summary-title">Submission Details</div>
        <div class="dole-inquiry__summary-grid">${detailsHTML}</div>
      `;
    }

    buildSummaryHTML(data, inquiryType, recipient) {
      return DoleInquiry.buildSummaryHTML(data, inquiryType, recipient, this.dataset?.membership);
    }

    handleEmailDraft(recipient, subject, body, data) {
      const url = `mailto:${encodeURIComponent(recipient)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      if (this.preview) this.preview.value = `To: ${recipient}\nSubject: ${subject}\n\n${body}`;
      const openEmailBtn = this.querySelector('[data-open-email]');
      if (openEmailBtn) openEmailBtn.href = url;
      if (this.copyStatus) this.copyStatus.textContent = '';
      this.copyVersion = (this.copyVersion || 0) + 1;

      const heading = this.querySelector('[data-feedback-heading]');
      if (heading) heading.textContent = this.messages.ready || 'Your inquiry is ready';
      const badge = this.querySelector('[data-feedback-badge]');
      if (badge) badge.textContent = 'Inquiry Ready';
      const icon = this.querySelector('[data-feedback-icon]');
      if (icon) icon.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      const note = this.querySelector('[data-feedback-note]');
      if (note) note.textContent = this.messages.ready_note || 'Your email app should open with your details filled in.';

      const summaryBox = this.querySelector('[data-feedback-summary]');
      if (summaryBox && data) {
        summaryBox.innerHTML = this.buildSummaryHTML(data, this.dataset.inquiryType, recipient);
        summaryBox.hidden = false;
      }

      if (this.nextSteps) this.nextSteps.hidden = true;
      const retryBtn = this.querySelector('[data-retry]');
      if (retryBtn) retryBtn.hidden = true;
      const fallbackActions = this.querySelector('[data-fallback-actions]');
      if (fallbackActions) fallbackActions.hidden = false;
      const feedbackActions = this.querySelector('[data-feedback-actions]');
      if (feedbackActions) feedbackActions.hidden = true;

      const previewWrapper = this.querySelector('.dole-inquiry__preview');
      if (previewWrapper) previewWrapper.hidden = false;

      const panel = this.querySelector('.dole-inquiry__panel') || this;
      const initialHeight = panel.offsetHeight;
      if (initialHeight > 0) {
        panel.style.minHeight = `${initialHeight}px`;
      }

      this.form.hidden = true;
      this.feedback.hidden = false;
      this.setupShapes();
      this.feedback.focus({ preventScroll: true });
      DoleInquiry.scrollToElement(panel);

      setTimeout(() => {
        if (panel) {
          panel.style.transition = 'min-height 280ms cubic-bezier(0.16, 1, 0.3, 1)';
          panel.style.minHeight = '';
        }
      }, 380);
    }

    showLoading() {
      if (!this.loadingEl) return;
      if (this.form) this.form.hidden = true;
      if (this.feedback) this.feedback.hidden = true;
      this.loadingEl.hidden = false;
      if (typeof this.setupShapes === 'function') this.setupShapes();

      if (this.loadingBar) {
        this.loadingBar.classList.remove('is-finished');
        this.loadingBar.style.width = '0%';
        void this.loadingBar.offsetWidth;
        this.loadingBar.style.width = '92%';
      }

      if (this.loadingTitle) {
        this.loadingTitle.textContent = this.messages?.loading_title || 'Transmitting your inquiry...';
      }

      if (this.loadingStatus) {
        this.loadingStatus.style.opacity = '1';
        this.loadingStatus.textContent = this.messages?.loading_step_1 || 'Connecting to secure intake server...';
      }

      this.clearLoadingTimers();
      this.loadingTimers.push(setTimeout(() => {
        if (this.loadingStatus) {
          this.loadingStatus.style.opacity = '0';
          setTimeout(() => {
            if (this.loadingStatus) {
              this.loadingStatus.textContent = this.messages?.loading_step_2 || 'Encrypting details and dispatching...';
              this.loadingStatus.style.opacity = '1';
            }
          }, 180);
        }
      }, 1400));

      this.loadingTimers.push(setTimeout(() => {
        if (this.loadingStatus) {
          this.loadingStatus.style.opacity = '0';
          setTimeout(() => {
            if (this.loadingStatus) {
              const deptName = (this.dataset?.inquiryType === 'business') ? 'customer care' : 'academy';
              this.loadingStatus.textContent = this.messages?.loading_step_3 || `Verifying intake receipt with ${deptName} team...`;
              this.loadingStatus.style.opacity = '1';
            }
          }, 180);
        }
      }, 2600));
    }

    finishLoading() {
      if (this.loadingBar) {
        this.loadingBar.classList.add('is-finished');
      }
    }

    hideLoading() {
      this.clearLoadingTimers();
      if (this.loadingEl) {
        this.loadingEl.hidden = true;
      }
    }

    clearLoadingTimers() {
      if (Array.isArray(this.loadingTimers)) {
        this.loadingTimers.forEach(t => clearTimeout(t));
      }
      this.loadingTimers = [];
    }

    async handleAutoSend(recipient, subject, body, data) {
      const submitButton = this.form.querySelector?.('button[type="submit"]');
      const buttonText = submitButton?.querySelector?.('[data-button-text]') || submitButton?.querySelector?.('span');
      const spinner = submitButton?.querySelector?.('[data-button-spinner]');
      const arrow = submitButton?.querySelector?.('[data-button-arrow]');
      const originalButtonText = buttonText ? buttonText.textContent : '';
      const retryButton = this.querySelector?.('[data-retry]');
      const originalRetryText = retryButton ? retryButton.textContent : '';

      const panel = this.querySelector?.('.dole-inquiry__panel') || this;
      const initialHeight = panel?.offsetHeight || 0;
      if (initialHeight > 0 && panel.style) {
        panel.style.minHeight = `${initialHeight}px`;
      }

      if (submitButton) {
        submitButton.disabled = true;
        if (spinner) spinner.hidden = false;
        if (arrow) arrow.style.display = 'none';
        if (buttonText) buttonText.textContent = this.messages?.submitting || this.messages?.sending || 'Submitting inquiry...';
      }
      if (retryButton) {
        retryButton.disabled = true;
        retryButton.textContent = this.messages?.submitting || this.messages?.sending || 'Submitting inquiry...';
      }
      this.form.setAttribute?.('data-submitting', '');

      this.showLoading?.();
      DoleInquiry.scrollToElement(panel);

      const minDuration = (typeof this.loadingDuration === 'number') ? this.loadingDuration : 3800;
      const delayPromise = (minDuration > 0)
        ? new Promise(resolve => setTimeout(resolve, minDuration))
        : Promise.resolve();

      let fetchPromise;
      try {
        const formData = new FormData(this.form);
        const action = (typeof this.form.getAttribute === 'function' ? this.form.getAttribute('action') : this.form.action) || '/contact';

        fetchPromise = fetch(action, {
          method: 'POST',
          body: formData,
          headers: {
            'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8'
          }
        });
      } catch (err) {
        fetchPromise = Promise.reject(err);
      }

      try {
        const [response] = await Promise.all([fetchPromise, delayPromise]);

        if (response && response.redirected && response.url && response.url.includes('/challenge')) {
          this.hideLoading?.();
          this.form.submit();
          return;
        }

        const responseText = (response && typeof response.text === 'function') ? await response.text() : '';
        const hasError = responseText.includes('form-status--error') || (response && !response.ok && response.status >= 400);

        if (hasError) {
          throw new Error('Server returned error status');
        }

        this.finishLoading?.();
        if (minDuration > 0) {
          await new Promise(resolve => setTimeout(resolve, 280));
        }

        this.hideLoading?.();
        if (typeof this.transitionToSuccess === 'function') {
          this.transitionToSuccess(recipient, data, panel);
        } else {
          this.showSuccess?.(recipient, data);
        }
      } catch (error) {
        this.hideLoading?.();
        this.showSendFallback(recipient, subject, body, data);
      } finally {
        this.form.removeAttribute?.('data-submitting');
        if (submitButton) {
          submitButton.disabled = false;
          if (spinner) spinner.hidden = true;
          if (arrow) arrow.style.display = '';
          if (buttonText) buttonText.textContent = originalButtonText;
        }
        if (retryButton) {
          retryButton.disabled = false;
          retryButton.textContent = originalRetryText;
        }
      }
    }

    transitionToSuccess(recipient, data, panel) {
      const panelEl = panel || this.querySelector('.dole-inquiry__panel') || this;
      this.showSuccess(recipient, data);
      DoleInquiry.scrollToElement(panelEl);

      setTimeout(() => {
        if (panelEl) {
          panelEl.style.transition = 'min-height 280ms cubic-bezier(0.16, 1, 0.3, 1)';
          panelEl.style.minHeight = '';
        }
      }, 380);
    }

    showSuccess(recipient, data) {
      this.feedback.classList.remove('dole-inquiry__feedback--error');
      const heading = this.querySelector('[data-feedback-heading]');
      if (heading) heading.textContent = this.messages.sent_success || 'Inquiry submitted successfully';
      const badge = this.querySelector('[data-feedback-badge]');
      if (badge) badge.textContent = 'Inquiry Confirmed';
      const icon = this.querySelector('[data-feedback-icon]');
      if (icon) icon.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      const note = this.querySelector('[data-feedback-note]');
      if (note) note.textContent = this.messages?.sent_note || (this.dataset?.inquiryType === 'business' ? 'Inquiry received by the Lifeline Customer Care team at customercare@lifeline.com.ph.' : 'Inquiry received by the Lifeline EMS Academy team at academy@lifeline.com.ph.');

      const nextSteps = this.nextSteps || this.querySelector('[data-next-steps]');
      if (nextSteps) {
        if (this.messages?.next_steps_html) {
          nextSteps.innerHTML = this.messages.next_steps_html;
        }
        nextSteps.hidden = false;
      }

      const summaryBox = this.querySelector('[data-feedback-summary]');
      if (summaryBox && data) {
        summaryBox.innerHTML = this.buildSummaryHTML(data, this.dataset.inquiryType, recipient);
        summaryBox.hidden = false;
      }

      const fallbackActions = this.querySelector('[data-fallback-actions]');
      if (fallbackActions) fallbackActions.hidden = true;
      const feedbackActions = this.querySelector('[data-feedback-actions]');
      if (feedbackActions) feedbackActions.hidden = false;

      const previewWrapper = this.querySelector('.dole-inquiry__preview');
      if (previewWrapper) previewWrapper.hidden = true;

      this.form.hidden = true;
      this.feedback.hidden = false;
      this.setupShapes();
      this.feedback.focus({ preventScroll: true });
    }

    showSendFallback(recipient, subject, body, data) {
      this.feedback.classList.add('dole-inquiry__feedback--error');
      const heading = this.querySelector('[data-feedback-heading]');
      if (heading) heading.textContent = this.messages.send_failed_heading || 'Submission could not be completed';
      const badge = this.querySelector('[data-feedback-badge]');
      if (badge) badge.textContent = 'Delivery Issue';
      const icon = this.querySelector('[data-feedback-icon]');
      if (icon) icon.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/><path d="M12 8v4m0 4h.01" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>';
      const note = this.querySelector('[data-feedback-note]');
      if (note) note.textContent = this.messages.send_failed_note || 'We could not deliver your inquiry through the website right now. Your details are saved below so you can try again, send via your email app, or copy them directly.';

      const nextSteps = this.nextSteps || this.querySelector('[data-next-steps]');
      if (nextSteps) nextSteps.hidden = true;

      const summaryBox = this.querySelector('[data-feedback-summary]');
      if (summaryBox && data) {
        summaryBox.innerHTML = this.buildSummaryHTML(data, this.dataset.inquiryType, recipient);
        summaryBox.hidden = false;
      }

      const url = `mailto:${encodeURIComponent(recipient)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      if (this.preview) this.preview.value = `To: ${recipient}\nSubject: ${subject}\n\n${body}`;
      const openEmailBtn = this.querySelector('[data-open-email]');
      if (openEmailBtn) openEmailBtn.href = url;
      if (this.copyStatus) this.copyStatus.textContent = '';
      this.copyVersion = (this.copyVersion || 0) + 1;

      const retryBtn = this.querySelector('[data-retry]');
      if (retryBtn) retryBtn.hidden = false;
      const fallbackActions = this.querySelector('[data-fallback-actions]');
      if (fallbackActions) fallbackActions.hidden = false;
      const feedbackActions = this.querySelector('[data-feedback-actions]');
      if (feedbackActions) feedbackActions.hidden = true;

      const previewWrapper = this.querySelector('.dole-inquiry__preview');
      if (previewWrapper) previewWrapper.hidden = false;

      const panel = this.querySelector('.dole-inquiry__panel') || this;
      const initialHeight = panel.offsetHeight;
      if (initialHeight > 0) {
        panel.style.minHeight = `${initialHeight}px`;
      }

      this.form.hidden = true;
      this.feedback.hidden = false;
      this.setupShapes();
      this.feedback.focus({ preventScroll: true });
      DoleInquiry.scrollToElement(panel);

      setTimeout(() => {
        if (panel) {
          panel.style.transition = 'min-height 280ms cubic-bezier(0.16, 1, 0.3, 1)';
          panel.style.minHeight = '';
        }
      }, 380);
    }

    editForm() {
      this.hideLoading?.();
      const panel = this.querySelector('.dole-inquiry__panel') || this;
      const initialHeight = panel.offsetHeight;
      if (initialHeight > 0) {
        panel.style.minHeight = `${initialHeight}px`;
      }

      this.feedback.hidden = true;
      this.feedback.classList.remove('dole-inquiry__feedback--error');
      this.form.hidden = false;
      this.setupShapes();
      DoleInquiry.scrollToElement(panel);

      setTimeout(() => {
        if (panel) {
          panel.style.transition = 'min-height 280ms cubic-bezier(0.16, 1, 0.3, 1)';
          panel.style.minHeight = '';
        }
      }, 380);

      const firstInput = this.form.querySelector('input:not([type="hidden"]), select, textarea');
      firstInput?.focus({ preventScroll: true });
    }

    resetForm() {
      this.hideLoading?.();
      this.form.reset();
      const panel = this.querySelector('.dole-inquiry__panel') || this;
      const initialHeight = panel.offsetHeight;
      if (initialHeight > 0) {
        panel.style.minHeight = `${initialHeight}px`;
      }

      this.form.hidden = false;
      this.feedback.hidden = true;
      this.feedback.classList.remove('dole-inquiry__feedback--error');

      const icon = this.querySelector('[data-feedback-icon]');
      if (icon) icon.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      const summaryBox = this.querySelector('[data-feedback-summary]');
      if (summaryBox) {
        summaryBox.innerHTML = '';
        summaryBox.hidden = true;
      }

      const nextSteps = this.nextSteps || this.querySelector('[data-next-steps]');
      if (nextSteps) nextSteps.hidden = false;

      const quoteCard = this.querySelector('[data-quote-card]');
      if (quoteCard) {
        const quoteAmount = this.querySelector('[data-quote-amount]');
        const quoteDetails = this.querySelector('[data-quote-details]');
        const checkedCourse = this.querySelector('[data-field="training_type"]:checked, [name="training_type"]:checked, [name="contact[training_type]"]:checked');
        const participantsInput = this.querySelector('[data-field="participants"], [name="participants"], [name="contact[participants]"]');
        const count = parseInt(participantsInput?.value, 10);
        const rate = checkedCourse ? parseInt(checkedCourse.dataset.rate, 10) : 0;
        const courseCode = checkedCourse ? checkedCourse.dataset.course : '';

        if (checkedCourse && !isNaN(count) && count > 0) {
          const total = count * rate;
          if (quoteAmount) quoteAmount.textContent = '₱' + total.toLocaleString('en-PH');
          if (quoteDetails) quoteDetails.textContent = `${count} participant${count === 1 ? '' : 's'} × ₱${rate.toLocaleString('en-PH')} / head (${courseCode})`;
          quoteCard.classList.add('dole-inquiry__quote--active');
        } else if (checkedCourse) {
          if (quoteAmount) quoteAmount.textContent = '₱' + rate.toLocaleString('en-PH');
          if (quoteDetails) quoteDetails.textContent = `₱${rate.toLocaleString('en-PH')} per head · Enter number of participants for ${courseCode}`;
          quoteCard.classList.remove('dole-inquiry__quote--active');
        } else {
          if (quoteAmount) quoteAmount.textContent = '₱0';
          if (quoteDetails) quoteDetails.textContent = this.messages.quote_prompt || 'Select a course and enter participants';
          quoteCard.classList.remove('dole-inquiry__quote--active');
        }
      }

      const onsiteWrapper = this.querySelector('[data-onsite-wrapper]');
      const onsiteInput = this.querySelector('[data-field="training_location"], [name="training_location"], [name="contact[training_location]"]');
      if (onsiteWrapper && onsiteInput) {
        const isVenueOnsite = this.querySelector('[name="training_venue"]:checked, [name="contact[training_venue]"]:checked')?.value === 'onsite';
        onsiteWrapper.hidden = !isVenueOnsite;
        onsiteInput.required = isVenueOnsite;
      }

      this.form.querySelectorAll('[aria-invalid="true"]').forEach(el => el.removeAttribute('aria-invalid'));
      this.form.querySelectorAll('.dole-inquiry__error').forEach(el => { el.textContent = ''; el.hidden = true; });
      if (this.summary) this.summary.textContent = '';
      this.updateRequiredNames?.();
      this.setupShapes();
      DoleInquiry.scrollToElement(panel);

      setTimeout(() => {
        if (panel) {
          panel.style.transition = 'min-height 280ms cubic-bezier(0.16, 1, 0.3, 1)';
          panel.style.minHeight = '';
        }
      }, 380);

      const firstInput = this.form.querySelector('input:not([type="hidden"]), select, textarea');
      firstInput?.focus({ preventScroll: true });
    }

    static buildEmail(data, inquiryType, membership, source) {
      const fullName = (data.first_name && data.last_name)
        ? `${data.first_name} ${data.last_name}`.trim()
        : (data.name || `${data.first_name || ''} ${data.last_name || ''}`.trim());
      const mobileNumber = data.mobile || data.phone || '';
      if (inquiryType === 'business') {
        const selectedMembership = data.membership_type || membership || 'Corporate membership';
        const orgLines = [
          'ORGANIZATION & FACILITY',
          data.company ? `Company: ${data.company}` : null,
          data.industry ? `Industry: ${data.industry}` : null,
          data.employees ? `Number of Employees: ${data.employees}` : null,
          data.address ? `Company Address: ${data.address}` : null
        ].filter(Boolean);

        const planLines = [
          'MEMBERSHIP PLAN',
          `Membership: ${selectedMembership}`
        ];

        const contactLines = [
          'PRIMARY CONTACT',
          data.first_name ? `First Name: ${data.first_name}` : null,
          data.last_name ? `Last Name: ${data.last_name}` : null,
          (!data.first_name && !data.last_name && data.name) ? `Name: ${data.name}` : null,
          data.position ? `Position: ${data.position}` : null,
          data.email ? `Email Address: ${data.email}` : null,
          mobileNumber ? `Mobile Number: ${mobileNumber}` : null
        ].filter(Boolean);

        const contextLines = [
          'SUBMISSION CONTEXT',
          source ? `Page: ${source}` : null,
          'Intake Routing: Lifeline 16-911 Customer Care (customercare@lifeline.com.ph)'
        ].filter(Boolean);

        return {
          subject: `Lifeline 16-911 Corporate Membership Inquiry: ${selectedMembership} - ${data.company} (${fullName})`,
          body: [
            'LIFELINE 16-911 CORPORATE MEMBERSHIP INQUIRY', '',
            ...orgLines, '',
            ...planLines, '',
            ...contactLines, '',
            ...contextLines
          ].join('\n')
        };
      }

      const academyAddress = 'Valgosons Building, Lifeline Compound, 8484 East Service Road Km. 18, Sucat, Parañaque City, Philippines 1700';
      const venueText = (data.training_venue === 'onsite')
        ? `On-site (${data.training_location || 'Address to be specified'})`
        : `Lifeline EMS Academy (${academyAddress})`;

      let rate = 0;
      if (/EFAT/i.test(data.training_type)) rate = 1500;
      else if (/OFAT/i.test(data.training_type)) rate = 2500;
      else if (/SFAT/i.test(data.training_type)) rate = 3500;

      const participantCount = parseInt(data.participants, 10);
      const quoteLines = [];
      if (rate > 0) {
        quoteLines.push(`Rate per Head: ₱${rate.toLocaleString('en-PH')} / participant`);
        if (!isNaN(participantCount) && participantCount > 0) {
          const total = rate * participantCount;
          quoteLines.push(`Estimated Total Quotation: ₱${total.toLocaleString('en-PH')} (${participantCount} participants × ₱${rate.toLocaleString('en-PH')} / head)`);
        }
      }

      const trainingLines = [
        'TRAINING SPECIFICATIONS',
        `Type of Training: ${data.training_type}`,
        `Number of Participants: ${data.participants}`,
        ...quoteLines,
        `Training Venue: ${venueText}`
      ];

      const trainingContactLines = [
        'ORGANIZATION & PRIMARY CONTACT',
        data.company ? `Company: ${data.company}` : null,
        data.first_name ? `First Name: ${data.first_name}` : null,
        data.last_name ? `Last Name: ${data.last_name}` : null,
        (!data.first_name && !data.last_name && data.name) ? `Name: ${data.name}` : null,
        data.position ? `Position: ${data.position}` : null,
        data.email ? `Email Address: ${data.email}` : null,
        mobileNumber ? `Mobile Number: ${mobileNumber}` : null
      ].filter(Boolean);

      const trainingContextLines = [
        'SUBMISSION CONTEXT',
        source ? `Page: ${source}` : null,
        'Intake Routing: Lifeline EMS Academy (academy@lifeline.com.ph)'
      ].filter(Boolean);

      return {
        subject: `DOLE Training Inquiry: ${data.training_type} - ${data.company} (${fullName})`,
        body: [
          'DOLE FIRST AID & BLS TRAINING INQUIRY', '',
          ...trainingLines, '',
          ...trainingContactLines, '',
          ...trainingContextLines
        ].join('\n')
      };
    }

    openEmail(url) { window.location.href = url; }

    async copy() {
      const text = this.preview.value;
      const version = this.copyVersion;
      if (!text || this.feedback.hidden) return;
      try {
        await navigator.clipboard.writeText(text);
        if (version === this.copyVersion) this.copyStatus.textContent = this.messages.copied;
      } catch {
        if (version !== this.copyVersion) return;
        this.querySelector('details').open = true;
        this.preview.focus();
        this.preview.select();
        let copied = false;
        try {
          copied = (typeof document !== 'undefined' && typeof document.execCommand === 'function')
            ? document.execCommand('copy')
            : false;
        } catch { copied = false; }
        this.copyStatus.textContent = copied ? this.messages.copied : this.messages.copy_failed;
      }
    }
  }
  customElements.define('dole-inquiry', DoleInquiry);
}
